import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { FaSync, FaUserEdit } from "react-icons/fa";
import PageHeader from "../../core/common/header/PageHeader";

interface StudentForm {
  firstName: string;
  lastName: string;
  email: string;
  whatsappNumber: string;
  aimInGermany: string;
  lastDegree: string;
  germanLanguageLevel: string;
  isActiveStudent: boolean;
  role: string;
  fathersName: string;
  mothersName: string;
  dateOfBirth: string;
  address: string;
}

const AIM_OPTIONS = [
  { value: "Ausbildung", label: "Ausbildung" },
  { value: "Bachelor", label: "Bachelor" },
  { value: "Masters", label: "Masters" },
];

const DEGREE_OPTIONS = [
  { value: "SSC", label: "SSC" },
  { value: "HSC", label: "HSC" },
  { value: "Diploma", label: "Diploma" },
  { value: "Bachelor", label: "Bachelor" },
  { value: "Masters", label: "Masters" },
];

const LANGUAGE_OPTIONS = [
  { value: "None", label: "None" },
  { value: "A1", label: "A1" },
  { value: "A2", label: "A2" },
  { value: "B1", label: "B1" },
  { value: "B2", label: "B2" },
  { value: "C1", label: "C1" },
];

export const ROLE_OPTIONS = [
  { value: "STUDENT", label: "Student" },
  { value: "ADMIN", label: "Admin" },
  { value: "MENTOR", label: "Mentor" },
  { value: "EMPLOYEE", label: "Employee" },
];

const DESTS: Record<string, { path: string; label: string }> = {
  students: { path: "/admin/manage-students", label: "Students" },
  mentors: { path: "/admin/manage-mentors", label: "Admins & Mentors" },
  staff: { path: "/admin/manage-mentors", label: "Admins & Mentors" },
};

const EditStudent: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const fromKey = useMemo(() => {
    const qs = new URLSearchParams(location.search);
    const v =
      qs.get("from") ||
      // @ts-ignore allow state-based back target as well
      (location.state && location.state.from) ||
      "students";
    return v as "students" | "mentors" | "staff";
  }, [location.search, location.state]);

  const backDest = DESTS[fromKey] ?? DESTS.students;

  const [form, setForm] = useState<StudentForm>({
    firstName: "",
    lastName: "",
    email: "",
    whatsappNumber: "",
    aimInGermany: "",
    lastDegree: "",
    germanLanguageLevel: "",
    isActiveStudent: false,
    role: "",
    fathersName: "",
    mothersName: "",
    dateOfBirth: "",
    address: "",
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchStudent = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/students/${id}`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error(`Load failed (${res.status})`);
      const data = await res.json();
      setForm({
        firstName: data.firstName ?? "",
        lastName: data.lastName ?? "",
        email: data.email ?? "",
        whatsappNumber: String(data.whatsappNumber ?? ""),
        aimInGermany: data.aimInGermany ?? "",
        lastDegree: data.lastDegree ?? "",
        germanLanguageLevel: data.germanLanguageLevel ?? "",
        isActiveStudent: Boolean(data.isActiveStudent ?? data.activeStudent),
        role: data.role ?? "",
        fathersName: data.fathersName ?? "",
        mothersName: data.mothersName ?? "",
        dateOfBirth: data.dateOfBirth ?? "",
        address: data.address ?? "",
      });
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudent();
  }, [id]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value, type, checked } = e.target as HTMLInputElement;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const goBack = () => navigate(backDest.path);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/students/${id}`,
        {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...form,
            whatsappNumber: Number(form.whatsappNumber || 0),
          }),
        },
      );
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `Save failed (${res.status})`);
      }
      goBack();
    } catch (e: any) {
      setError(e.message);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* NEW HEADER - Same as Admin Dashboard */}
      <PageHeader
        icon={<FaUserEdit />}
        title={`Edit User #${id}`}
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/admin/dashboard" },
          { label: backDest.label, to: backDest.path },
          { label: "Edit User" },
        ]}
      />

      <div className="flex justify-end gap-2 max-w-7xl mx-auto px-4 sm:px-6 pt-4">
        <button
          onClick={fetchStudent}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors disabled:opacity-50 text-sm backdrop-blur-sm"
          style={{ fontSize: "16px" }}
        >
          <FaSync
            className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
          />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>

      {/* Body */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="bg-white shadow-lg rounded-xl p-6 sm:p-8">
          {loading ? (
            <div className="text-center py-12">
              <FaSync className="animate-spin h-12 w-12 text-[#009fe3] mx-auto mb-4" />
              <p className="text-gray-600">Loading user details...</p>
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded">
                  <p className="font-semibold">Error</p>
                  <p>{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      name="firstName"
                      value={form.firstName}
                      onChange={handleChange}
                      placeholder="First Name"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                      style={{ fontSize: "16px" }}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      name="lastName"
                      value={form.lastName}
                      onChange={handleChange}
                      placeholder="Last Name"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                      style={{ fontSize: "16px" }}
                      required
                    />
                  </div>
                </div>

                {/* Email & WhatsApp */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="email@example.com"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                      style={{ fontSize: "16px" }}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      WhatsApp Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      name="whatsappNumber"
                      value={form.whatsappNumber}
                      onChange={handleChange}
                      placeholder="WhatsApp Number"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                      style={{ fontSize: "16px" }}
                      required
                    />
                  </div>
                </div>

                {/* Dropdowns */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Aim in Germany <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="aimInGermany"
                      value={form.aimInGermany}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                      style={{ fontSize: "16px" }}
                      required
                    >
                      {!form.aimInGermany && (
                        <option value="">Select Aim in Germany</option>
                      )}
                      {AIM_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Degree <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="lastDegree"
                      value={form.lastDegree}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                      style={{ fontSize: "16px" }}
                      required
                    >
                      {!form.lastDegree && (
                        <option value="">Select Last Degree</option>
                      )}
                      {DEGREE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* German Level & Role */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      German Level <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="germanLanguageLevel"
                      value={form.germanLanguageLevel}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                      style={{ fontSize: "16px" }}
                      required
                    >
                      {!form.germanLanguageLevel && (
                        <option value="">Select German Level</option>
                      )}
                      {LANGUAGE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Role <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="role"
                      value={form.role}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                      style={{ fontSize: "16px" }}
                      required
                    >
                      <option value="">Select Role</option>
                      {ROLE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Father / Mother */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Father's Name
                    </label>
                    <input
                      name="fathersName"
                      value={form.fathersName}
                      onChange={handleChange}
                      placeholder="Father's Name"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                      style={{ fontSize: "16px" }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mother's Name
                    </label>
                    <input
                      name="mothersName"
                      value={form.mothersName}
                      onChange={handleChange}
                      placeholder="Mother's Name"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                      style={{ fontSize: "16px" }}
                    />
                  </div>
                </div>

                {/* Date of Birth & Address */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date of Birth
                    </label>
                    <input
                      type="date"
                      name="dateOfBirth"
                      value={form.dateOfBirth}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                      style={{ fontSize: "16px" }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Complete Address
                    </label>
                    <input
                      name="address"
                      value={form.address}
                      onChange={handleChange}
                      placeholder="Full address"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                      style={{ fontSize: "16px" }}
                    />
                  </div>
                </div>

                {/* Active Status */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      name="isActiveStudent"
                      checked={form.isActiveStudent}
                      onChange={handleChange}
                      className="w-4 h-4 text-[#009fe3] focus:ring-[#009fe3] border-gray-300 rounded"
                    />
                    <div>
                      <span className="font-medium text-gray-900">
                        Active Student
                      </span>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Check to mark this user as an active student
                      </p>
                    </div>
                  </label>
                </div>

                {/* Save Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 bg-[#009fe3] hover:bg-[#007cb5] text-white py-3 rounded-lg font-semibold shadow-md hover:shadow-lg transition-colors"
                  >
                    Save Changes
                  </button>
                  <button
                    type="button"
                    onClick={goBack}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold shadow-md transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default EditStudent;
